<template>
  <!-- 父容器仅用于防止边缘溢出 -->
  <div :class="['overflow-hidden relative', 'size-full']">
    <ClientOnly>
      <canvas
        id="canvas"
        ref="canvasEl"
        :class="['absolute', 'size-full', 'scale-[1.3]']"
        :style="{
          filter: `blur(${props.blur}px)`,
        }"
      />
      <template #fallback>
        <!-- 服务端占位 -->
        <div :class="['size-full', 'bg-secondary']" />
      </template>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
  import { createNoise3D } from 'simplex-noise'

  // 背景色
  const bg_color_primary = 'rgb(246, 143, 30)'
  const bg_color_secondary = 'rgb(196, 18, 48)'

  interface WavyBackgroundProps {
    colors?: string[]
    waveWidth?: number
    backgroundFill?: string
    blur?: number
    speed?: 'slow' | 'fast'
    waveOpacity?: number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any
  }

  const props: any = withDefaults(defineProps<WavyBackgroundProps>(), {
    colors: () => [bg_color_primary, bg_color_secondary],
    waveWidth: 24,
    backgroundFill: bg_color_secondary,
    blur: 20,
    speed: 'fast',
    waveOpacity: 0.8,
  })

  const noise = createNoise3D()

  // Declare variables with null
  let w: number,
    h: number,
    nt = 0,
    ctx: CanvasRenderingContext2D | null = null
  let animationId: number

  const canvasEl = useTemplateRef('canvasEl')

  function getSpeed(): number {
    return props.speed === 'slow' ? 0.001 : 0.002
  }

  function init() {
    const canvas = canvasEl.value
    if (!canvas) return useDPrint('⭐wavy未找到')
    useDPrint('⭐wavy水合触发')
    ctx = canvas.getContext('2d')
    if (ctx) {
      const parent = canvasEl.value.parentElement
      if (parent) {
        w = ctx.canvas.width = parent.clientWidth
        h = ctx.canvas.height = parent.clientHeight
      }
      ctx.filter = `blur(${props.blur}px)`
      window.onresize = () => {
        if (parent) {
          w = ctx!.canvas.width = parent.clientWidth
          h = ctx!.canvas.height = parent.clientHeight
        }
        ctx!.filter = `blur(${props.blur}px)`
      }
      render()
    }
  }

  function drawWave(n: number) {
    nt += getSpeed()
    for (let i = 0; i < n; i++) {
      ctx!.beginPath()
      ctx!.lineWidth = props.waveWidth!
      ctx!.strokeStyle = props.colors[i % props.colors.length]
      for (let x = 0; x < w; x += 5) {
        const y = noise(x / 800, 0.3 * i, nt) * 100
        ctx!.lineTo(x, y + h * 0.5) // Adjust for height, at 50% of the container
      }
      ctx!.stroke()
      ctx!.closePath()
    }
  }

  function render() {
    if (ctx) {
      ctx.fillStyle = props.backgroundFill!
      ctx.globalAlpha = props.waveOpacity!
      ctx.fillRect(0, 0, w, h)
      drawWave(5)
      animationId = requestAnimationFrame(render)
    }
  }

  onBeforeUnmount(() => {
    cancelAnimationFrame(animationId)
  })

  onMounted(async () => {
    await nextTick()
    init()
  })
</script>
